import { useEffect, useMemo } from 'react'
import { isAxiosError } from 'axios'
import { LatLngBounds } from 'leaflet'
import { Activity, MapPinned, RefreshCw } from 'lucide-react'
import {
  CircleMarker,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Kpi } from '@/components/ui/kpi'
import { SkeletonRows } from '@/components/ui/skeleton'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import { useMe } from '@/features/auth/api'
import { Link } from '@/lib/router'
import { relativeTime } from '@/lib/format'

import {
  type TripReviewItem,
  type TripTrailResponse,
  useLiveTrips,
  useLiveTripTrails,
} from './api'

/**
 * Live multi-trip monitor (slice T8).
 *
 * One map with every currently-ACTIVE trip whose agent is inside the
 * viewer's TRIP_LIVE_VIEW scope (granted per role in Roles & permissions;
 * Tenant Admin holds it by default). Each agent gets:
 *   - a polyline tracing their trail so far,
 *   - a marker at the latest GPS fix,
 *   - a popup with empId, distance covered, and a deep-link to the
 *     trip detail page.
 *
 * Active list refreshes every 30s; each trail polls every 15s
 * independently. The map auto-fits to enclose every active agent on
 * mount and whenever the agent set changes (a trip ending or a new
 * one starting).
 */
export function LiveTripsPage() {
  const review = useLiveTrips()
  // Cosmetic: the trip detail endpoints enforce their own gate.
  const { data: me } = useMe()
  const canOpenTrip = navPermissionCheck(me)('TRIP_DETAIL_VIEW')
  const items = useMemo(() => review.data?.items ?? [], [review.data])
  const tripIds = useMemo(() => items.map((i) => i.tripId), [items])
  const trails = useLiveTripTrails(tripIds)

  const points = useMemo(() => collectAgentPositions(items, trails), [items, trails])

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Activity className="h-5 w-5" />
            Live visits
          </h1>
          <p className="text-sm text-muted-foreground">
            Every agent you can see out on visits right now, refreshing every 15
            seconds. Markers sit at the latest GPS fix; polylines trace the
            trail so far.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => review.refetch()}
          disabled={review.isFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${review.isFetching ? 'animate-spin' : ''}`} />
          Refresh now
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi label="Active visit plans" value={items.length} />
        <Kpi
          label="Agents on the field"
          value={new Set(items.map((i) => i.agentUserId)).size}
        />
        <Kpi
          label="Most recent start"
          value={
            items.length === 0
              ? '—'
              : relativeTime(latestStart(items)) ?? '—'
          }
          hint="time since the newest visits began"
        />
      </div>

      {review.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load active visits</AlertTitle>
          <AlertDescription>
            {isAxiosError(review.error) && review.error.response?.status === 403
              ? "You don't have the Live Visit permission. Ask an admin to enable it in Roles & permissions."
              : isAxiosError(review.error)
                ? review.error.message
                : String(review.error)}
          </AlertDescription>
        </Alert>
      )}

      {!review.isError && (
      <Card>
        <CardHeader className="space-y-1">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Map
          </h2>
          <p className="text-xs text-muted-foreground">
            {items.length === 0 && !review.isLoading
              ? 'No agents are out on visits right now. The map will appear when one starts.'
              : `${points.length} agent${points.length === 1 ? '' : 's'} plotted.`}
          </p>
        </CardHeader>
        <CardContent>
          {review.isLoading ? (
            <SkeletonRows rows={4} />
          ) : items.length === 0 ? (
            <div className="grid h-72 place-items-center rounded-md border border-dashed bg-slate-50 text-sm text-muted-foreground">
              No active visits.
            </div>
          ) : (
            <LiveMap items={items} trails={trails} canOpenTrip={canOpenTrip} />
          )}
        </CardContent>
      </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

interface AgentPoint {
  tripId: string
  agentUserId: string
  position: [number, number]
  polyline: [number, number][]
  fixCount: number
}

function collectAgentPositions(
  items: TripReviewItem[],
  trails: Record<string, TripTrailResponse | undefined>,
): AgentPoint[] {
  const out: AgentPoint[] = []
  for (const item of items) {
    const trail = trails[item.tripId]
    const polyline = (trail?.fixes ?? [])
      .map((f) => {
        if (f.lat === null || f.lng === null) return null
        const lat = parseFloat(f.lat)
        const lng = parseFloat(f.lng)
        return Number.isFinite(lat) && Number.isFinite(lng)
          ? ([lat, lng] as [number, number])
          : null
      })
      .filter((p): p is [number, number] => p !== null)

    let position: [number, number] | null = null
    if (polyline.length > 0) {
      position = polyline[polyline.length - 1]!
    } else if (item.startedLat && item.startedLng) {
      const lat = parseFloat(item.startedLat)
      const lng = parseFloat(item.startedLng)
      if (Number.isFinite(lat) && Number.isFinite(lng)) position = [lat, lng]
    }
    if (!position) continue

    out.push({
      tripId: item.tripId,
      agentUserId: item.agentUserId,
      position,
      polyline,
      fixCount: polyline.length,
    })
  }
  return out
}

function latestStart(items: TripReviewItem[]): string | null {
  let latest: string | null = null
  for (const i of items) {
    if (!i.startedAt) continue
    if (latest === null || i.startedAt > latest) latest = i.startedAt
  }
  return latest
}

// ---------------------------------------------------------------------------

function LiveMap({
  items,
  trails,
  canOpenTrip,
}: {
  items: TripReviewItem[]
  trails: Record<string, TripTrailResponse | undefined>
  canOpenTrip: boolean
}) {
  const points = useMemo(() => collectAgentPositions(items, trails), [items, trails])

  // Initial centre: just use the first plotted agent. The
  // FitToPoints child below adjusts bounds in an effect, so
  // re-centring on update is handled there.
  const initialCentre: [number, number] = points[0]?.position ?? [12.971, 77.594]

  if (points.length === 0) {
    return (
      <div className="grid h-72 place-items-center rounded-md border border-dashed bg-slate-50 text-sm text-muted-foreground">
        No GPS fixes captured yet.
      </div>
    )
  }

  return (
    <div className="h-[560px] overflow-hidden rounded-md border">
      <MapContainer
        center={initialCentre}
        zoom={13}
        scrollWheelZoom
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToPoints points={points.map((p) => p.position)} />
        {points.map((p) => (
          <AgentLayer key={p.tripId} point={p} canOpenTrip={canOpenTrip} />
        ))}
      </MapContainer>
    </div>
  )
}

function FitToPoints({ points }: { points: [number, number][] }) {
  const map = useMap()
  // Stringify so identity-equal updates short-circuit.
  const key = points.map((p) => `${p[0]},${p[1]}`).join('|')
  useEffect(() => {
    if (points.length === 0) return
    if (points.length === 1) {
      map.setView(points[0]!, Math.max(map.getZoom(), 13))
      return
    }
    const bounds = new LatLngBounds(points)
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return null
}

function AgentLayer({ point, canOpenTrip }: { point: AgentPoint; canOpenTrip: boolean }) {
  return (
    <>
      {point.polyline.length > 1 && (
        <Polyline
          positions={point.polyline}
          pathOptions={{ color: '#1d4ed8', weight: 3, opacity: 0.7 }}
        />
      )}
      <CircleMarker
        center={point.position}
        radius={9}
        pathOptions={{
          color: '#1d4ed8',
          fillColor: '#3b82f6',
          fillOpacity: 0.9,
          weight: 2,
        }}
      >
        <Popup>
          <div className="space-y-1 text-xs">
            <div className="font-semibold">Agent {shortId(point.agentUserId)}</div>
            <div className="text-muted-foreground">
              {point.fixCount === 0
                ? 'Start fix only — no GPS yet.'
                : `${point.fixCount} fix${point.fixCount === 1 ? '' : 'es'} so far.`}
            </div>
            {canOpenTrip && (
              <Link
                to="/trips/$tripId"
                params={{ tripId: point.tripId }}
                className="inline-flex items-center gap-1 text-info hover:underline"
              >
                <MapPinned className="h-3 w-3" /> Open visit plan
              </Link>
            )}
            <div className="pt-1">
              <Badge tone="info">ACTIVE</Badge>
            </div>
          </div>
        </Popup>
      </CircleMarker>
    </>
  )
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}
